/**
 * Список фич доски с группировкой по layer_group; клик ↔ select на карте.
 */
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { cn } from "@/lib/utils";
import type { TacticalFeature, TacticalLayerGroup } from "@/services/tactical.service";

type Props = {
  features: TacticalFeature[];
  groups: TacticalLayerGroup[];
  selectedIds: number[];
  onSelect: (featureId: number) => void;
};

function featureTitle(f: TacticalFeature): string {
  const p = f.properties;
  const label = (p?.label ?? "").trim();
  if (label) return label;
  const name = (p?.name ?? "").trim();
  if (name) return name;
  return `${p?.kind ?? "feature"} #${p?.id}`;
}

export function TacticalFeatureList({ features, groups, selectedIds, onSelect }: Props) {
  const selected = new Set(selectedIds);
  const byGroup = new Map<number | null, TacticalFeature[]>();
  byGroup.set(null, []);
  for (const g of groups) byGroup.set(g.id, []);
  for (const f of features) {
    const gid = f.properties?.layerGroupId ?? null;
    const key = gid != null && byGroup.has(gid) ? gid : null;
    byGroup.get(key)!.push(f);
  }

  const sections: Array<{ key: string; title: string; items: TacticalFeature[] }> = [];
  for (const g of groups) {
    const items = byGroup.get(g.id) ?? [];
    if (items.length === 0) continue;
    sections.push({ key: `g-${g.id}`, title: g.name, items });
  }
  const ungrouped = byGroup.get(null) ?? [];
  if (ungrouped.length > 0 || sections.length === 0) {
    sections.push({
      key: "ungrouped",
      title: groups.length ? "Без группы" : "Фичи",
      items: ungrouped,
    });
  }

  return (
    <div className="space-y-2 border-t pt-2">
      <div className="flex items-center gap-1 px-1">
        <p className="text-xs font-medium text-muted-foreground">Объекты</p>
        <SectionHelpTooltip label="Справка: список фич">
          <p>Список фич открытой доски. Клик выбирает объект на карте (и наоборот).</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Группировка — по группам слоёв слева выше.</li>
            <li>Esc / клик мимо на карте снимает выбор.</li>
          </ul>
        </SectionHelpTooltip>
      </div>
      {features.length === 0 ? (
        <p className="px-1 text-[10px] text-muted-foreground">Нет фич на доске</p>
      ) : (
        <div className="max-h-48 space-y-2 overflow-y-auto px-1">
          {sections.map((sec) => (
            <div key={sec.key}>
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                {sec.title}
              </p>
              <ul className="space-y-0.5">
                {sec.items.map((f) => {
                  const id = Number(f.properties?.id);
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        className={cn(
                          "w-full rounded px-1.5 py-1 text-left text-xs hover:bg-muted",
                          selected.has(id) && "bg-muted font-medium",
                        )}
                        onClick={() => onSelect(id)}
                      >
                        <span className="block truncate">{featureTitle(f)}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {f.properties?.kind ?? "?"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
